import { Link } from "react-router-dom";
import { Instagram, Twitter, Facebook, Youtube, MessageCircle, Send, Linkedin, Globe } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useStore } from "@/contexts/StoreContext";

const SOCIAL_ITEMS = [
  { key: "facebook",  Icon: Facebook,      label: "Facebook"  },
  { key: "instagram", Icon: Instagram,     label: "Instagram" },
  { key: "youtube",   Icon: Youtube,       label: "YouTube"   },
  { key: "whatsapp",  Icon: MessageCircle, label: "WhatsApp"  },
  { key: "telegram",  Icon: Send,          label: "Telegram"  },
  { key: "twitter",   Icon: Twitter,       label: "Twitter"   },
  { key: "linkedin",  Icon: Linkedin,      label: "LinkedIn"  },
  { key: "website",   Icon: Globe,         label: "Website"   },
];

export default function CustomerFooter() {
  const { social } = useStore();
  const links = SOCIAL_ITEMS.filter((s) => (social?.[s.key] || "").trim().length > 0);
  return (
    <footer className="border-t bg-secondary/40 mt-16 pb-20 md:pb-8" data-testid="customer-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-2">
          <BrandLogo size={28} tagline />
          <p className="text-sm text-muted-foreground max-w-xs mt-4">
            Your neighbourhood shop, online. Fresh essentials, delivered from Babu Store in minutes.
          </p>
          {links.length > 0 && (
            <div className="flex items-center gap-3 mt-4 text-muted-foreground flex-wrap" data-testid="footer-social">
              {links.map(({ key, Icon, label }) => (
                <a
                  key={key}
                  href={social[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="w-8 h-8 grid place-items-center rounded-full border border-border/60 hover:text-primary hover:border-primary transition"
                  data-testid={`footer-social-${key}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Sprint 15 #11 — Useful Links (moved from top nav) */}
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Useful links</div>
          <ul className="space-y-2 text-sm" data-testid="footer-useful-links">
            <li><Link to="/"                className="hover:text-primary" data-testid="ful-home">Home</Link></li>
            <li><Link to="/category"        className="hover:text-primary" data-testid="ful-categories">Categories</Link></li>
            <li><Link to="/shop"            className="hover:text-primary" data-testid="ful-shop">Shop all</Link></li>
            <li><Link to="/shop?featured=1" className="hover:text-primary" data-testid="ful-trending">Trending</Link></li>
            <li><Link to="/shop?tag=offers" className="hover:text-primary" data-testid="ful-offers">Offers</Link></li>
            <li><Link to="/wishlist"        className="hover:text-primary" data-testid="ful-wishlist">Wishlist</Link></li>
            <li><Link to="/about"           className="hover:text-primary" data-testid="ful-about">About</Link></li>
            <li><Link to="/contact"         className="hover:text-primary" data-testid="ful-contact">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/account/orders" className="hover:text-primary">Track order</Link></li>
            <li><Link to="/privacy" className="hover:text-primary">Privacy policy</Link></li>
            <li><Link to="/about"   className="hover:text-primary">Terms</Link></li>
            <li><Link to="/contact" className="hover:text-primary">Refunds</Link></li>
            {/* Subtle Staff Login entry — surfaces the admin login without
                cluttering the primary customer footer. */}
            <li>
              <Link
                to="/login"
                className="text-muted-foreground/70 hover:text-primary text-xs"
                data-testid="footer-staff-login"
              >
                Staff login
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Store</div>
          <div className="text-sm text-muted-foreground space-y-1">
            <div>Babu Store</div>
            <div>Support: 10am – 10pm</div>
            <div>hello@babubaazar.in</div>
          </div>
        </div>
      </div>

      <div className="border-t">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© 2026 Babu Baazar Retail Pvt Ltd. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-primary">Privacy</Link>
            <span>·</span>
            <Link to="/about" className="hover:text-primary">Terms</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-primary">Refunds</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
